import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * LR HUB own profile-card stats.
 *
 * Reads ONLY `hub_posts` (the caller's own rows): approved + non-deleted
 * posts, and the caller's own approved, non-deleted comments on
 * `hub_post_comments`.
 *
 * ISOLATION: Hub tables only. No profiles / companies / billing reads.
 */

export interface HubMyStats {
  /** Approved, non-deleted posts by the caller. */
  posts: number;
  /** Approved, non-deleted comments the caller has made. */
  comments: number;
}

export function useHubMyStats() {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  return useQuery({
    queryKey: ["hub-my-stats", userId ?? "anon"],
    enabled: !!userId,
    staleTime: 30_000,
    queryFn: async (): Promise<HubMyStats> => {
      const [{ data: posts, error: postsError }, { data: comments, error: commentsError }] = await Promise.all([
        supabase
          .from("hub_posts")
          .select("id")
          .eq("author_id", userId!)
          .eq("moderation_status", "approved")
          .eq("is_deleted", false),
        supabase
          .from("hub_post_comments")
          .select("id")
          .eq("author_id", userId!)
          .eq("moderation_status", "approved")
          .eq("is_deleted", false),
      ]);

      if (postsError) throw postsError;
      if (commentsError) throw commentsError;

      return { posts: (posts ?? []).length, comments: (comments ?? []).length };
    },
  });
}

export default useHubMyStats;
